import { ChangeDetectionStrategy, Component, TemplateRef, computed, input } from '@angular/core';
import { PortfolioBusinessCaseCard } from '@cfp/shared/portfolio/business-case';
import { PortfolioLocationsMap } from '@cfp/shared/portfolio/locations-map';
import { TranslateModule } from '@ngx-translate/core';
import {
  PortfolioBuildingsRoadmapBuildingDTO,
  PortfolioBuildingsRoadmapDTO,
  PortfolioBusinessCaseDTO
} from 'csr-asset-app';
import { ReportBuildingsLegend, ReportBuildingsTable } from '../../components';
import { ReportPage } from '../report-page';

const MAX_BUILDINGS_FIRST_PAGE = 15;
const MAX_BUILDINGS_ADDITIONAL_PAGES = 20;

@Component({
  selector: 'cfp-report-buildings-pages',
  templateUrl: 'report-buildings-pages.html',
  styleUrls: ['report-buildings-pages.scss'],
  imports: [
    ReportPage,
    PortfolioBusinessCaseCard,
    PortfolioLocationsMap,
    ReportBuildingsTable,
    ReportBuildingsLegend,
    TranslateModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportBuidingsPages {
  public readonly portfolioId = input.required<number>();
  public readonly customerName = input.required<string>();
  public readonly portfolioBuildingsRoadmap = input.required<PortfolioBuildingsRoadmapDTO>();
  public readonly translationKeyPrefix = input('portfolioBuildingsRoadmapReport');
  public readonly headerLogo = input<string | undefined>(undefined);
  public readonly chromeFooter = input<TemplateRef<unknown> | undefined>(undefined);

  protected readonly businessCase = computed(() => {
    const portfolioBuildingsRoadmap = this.portfolioBuildingsRoadmap();
    if (!portfolioBuildingsRoadmap) {
      return undefined;
    }

    const businessCase: Partial<PortfolioBusinessCaseDTO> = {
      costReduction: portfolioBuildingsRoadmap.businessCaseForBuildingsWithRoadmap.totalCostReduction,
      investment: portfolioBuildingsRoadmap.businessCaseForBuildingsWithRoadmap.totalInvestment,
      co2ConsumptionReduction:
        portfolioBuildingsRoadmap.businessCaseForBuildingsWithRoadmap.totalYearlyCo2ConsumptionReduction,
      area: portfolioBuildingsRoadmap.businessCase.totalArea,
      co2ConsumptionReductionPercentage:
        portfolioBuildingsRoadmap.businessCaseForBuildingsWithRoadmap.co2ConsumptionReductionPercentage,
      currencyCode: portfolioBuildingsRoadmap.currencyCode
    };
    return businessCase as PortfolioBusinessCaseDTO;
  });

  protected readonly pages = computed(() => {
    const portfolioBuildingsRoadmap = this.portfolioBuildingsRoadmap();
    if (!portfolioBuildingsRoadmap) {
      return [];
    }

    const groups: PortfolioBuildingsRoadmapBuildingDTO[][] = [];
    let currentGroup: PortfolioBuildingsRoadmapBuildingDTO[] = [];
    let isFirstGroup = true;

    portfolioBuildingsRoadmap.buildings.forEach((portfolioBuildingsRoadmap) => {
      if (isFirstGroup && currentGroup.length === MAX_BUILDINGS_FIRST_PAGE) {
        groups.push(currentGroup);
        currentGroup = [];
        isFirstGroup = false;
      } else if (!isFirstGroup && currentGroup.length === MAX_BUILDINGS_ADDITIONAL_PAGES) {
        groups.push(currentGroup);
        currentGroup = [];
      }
      currentGroup.push(portfolioBuildingsRoadmap);
    });

    if (currentGroup.length > 0) {
      groups.push(currentGroup);
    }

    return groups;
  });

  protected translationKey(suffix: string): string {
    // Content keys always use the shared namespace (LBG overrides via custom i18n).
    // translationKeyPrefix is only for report-page chrome title (buildings vs RETLL).
    return `portfolioBuildingsRoadmapReport.${suffix}`;
  }
}
